import SandboxBehavior from './SandboxBehavior.js';

export default class OnboardingBehavior extends SandboxBehavior {
    static ZONE_LAYOUT = {
        rows: 1,
        columns: 1,
        spacing_x: 0,
        spacing_y: 0,
        zones: [
            {
                id: 'onboarding_zone_1',
                title: 'Put all cards here',
                x: 5.1,
                y: -0.7,
                width: 3.4,
                height: 3.2,
                color: '#e6dcc8',
                bevel: 0.2,
                border: 0,
                font_size: 24,
                font_color: '#31404a',
                font_color_contour: '#ffffff',
            },
        ],
    };

    constructor(space, interaction, runtimeState = {}) {
        super(space, interaction, runtimeState);
        this.initialCardOrder = [];
        this.localCardZones = {};
        this.state = this._normalizeState(runtimeState);
        this.localEventsBound = false;
        this.flippedCardUpChangedBound = this._handleLocalFlippedCardUp.bind(this);
        this.defocusedCardChangedBound = this._handleLocalDefocusedCard.bind(this);
    }

    onSetup(cards) {
        this.initialCardOrder = [...cards]
            .sort((a, b) => a.index - b.index)
            .map(card => card.id);

        this._bindLocalEvents();
        this.applyState(this.runtimeState);
        this._syncIntroCardPresentation();
    }

    onDestroy() {
        this._unbindLocalEvents();
    }

    applyState(state) {
        super.applyState(state);
        this.state = this._normalizeState(state);
        this.localCardZones = { ...(this.state.card_zones || {}) };
        this._applyZoneVisibility();
        this._syncIntroCardPresentation();
    }

    canUseClickMove() {
        return true;
    }

    canUseFocusToggle() {
        return true;
    }

    canUsePinchFocus() {
        return false;
    }

    onTap(card, position) {
        if (this.state.completed) return;
        if (card && !this._isUnlocked(card) && !this.space.focusedCard) return;

        const previousSelected = this.space.selectedCard;
        const wasSelected = !!card?.isSelected;

        super.onTap(card, position);

        this._syncDeselectedCardZone(previousSelected);

        if (card && !wasSelected && this.space.selectedCard?.id === card.id) {
            this._maybeAdvanceFromSelection(card);
        }
    }

    onDoubleTap(card, position) {
        if (this.state.completed) return;

        if (!this.space.focusedCard && card && !this._isUnlocked(card)) return;

        super.onDoubleTap(card, position);
    }

    onPanStart(card, position) {
        if (this.state.completed) return false;
        if (card && !this._isUnlocked(card)) return false;

        const previousSelected = this.space.selectedCard;
        const wasSelected = !!card?.isSelected;
        const result = super.onPanStart(card, position);

        this._syncDeselectedCardZone(previousSelected);

        if (result === true && card && !wasSelected && this.space.selectedCard?.id === card.id) {
            this._maybeAdvanceFromSelection(card);
        }

        return result;
    }

    onPanMove(card, position) {
        if (this.state.completed) return;
        super.onPanMove(card, position);
    }

    onPanEnd(card, position) {
        if (this.state.completed) return;

        const selectedCard = this.space.selectedCard;
        super.onPanEnd(card, position);

        if (selectedCard) {
            this._syncCardZone(selectedCard);
        }
    }

    onMouseMove(card, position) {
        const unlockedCard = card && this._isUnlocked(card) ? card : null;
        super.onMouseMove(unlockedCard, position);
    }

    onScrollWheel(card, direction) {
        if (this.state.completed) return;

        if (!this.space.focusedCard) {
            const target = this.space.highlightedCard || this.space.selectedCard || this.space.highLiftedCard;
            if (target && !this._isUnlocked(target)) return;
        }

        super.onScrollWheel(card, direction);
    }

    onKeyDown(card, event) {
        if (this.state.completed) return;

        if (event.code === 'Space' && this._isIntroPresentationActive()) return;

        const target = this.space.focusedCard
            || this.space.selectedCard
            || this.space.highlightedCard
            || this.space.highLiftedCard;

        if (target && !this._isUnlocked(target)) return;

        super.onKeyDown(card, event);
    }

    onKeyUp(event) {
        super.onKeyUp(event);
    }

    onWindowBlur() {
        super.onWindowBlur();
    }

    _bindLocalEvents() {
        if (this.localEventsBound || !this.space?.eventBus) return;

        this.space.eventBus.on('flippedCardUp', this.flippedCardUpChangedBound);
        this.space.eventBus.on('defocusedCard', this.defocusedCardChangedBound);
        this.localEventsBound = true;
    }

    _unbindLocalEvents() {
        if (!this.localEventsBound || !this.space?.eventBus) return;

        this.space.eventBus.off('flippedCardUp', this.flippedCardUpChangedBound);
        this.space.eventBus.off('defocusedCard', this.defocusedCardChangedBound);
        this.localEventsBound = false;
    }

    _handleLocalFlippedCardUp(event) {
        const cardId = event?.detail?.id;
        const cardNumber = this._getCardNumber(cardId);

        if (cardNumber === 2) {
            this._advanceLocalState({ unlocked_card_number: 3 });
        }

        if (cardNumber === 3) {
            this._advanceLocalState({ unlocked_card_number: 5 });
        }

        if (cardNumber === 5) {
            this._advanceLocalState({ zone_visible: true });
        }
    }

    _handleLocalDefocusedCard(event) {
        const cardId = event?.detail?.id;

        if (!this._isIntroCard(cardId) || this.state.unlocked_card_number > 1) return;

        const introCard = this.space.getCard(cardId);
        introCard.isHighLifted = false;

        if (this.space.highLiftedCard?.id === cardId) {
            this.space.highLiftedCard = null;
        }

        this._advanceLocalState({ unlocked_card_number: 2 });
        this.space.eventBus.pushRemote('onboardingIntroDismissed', {
            id: cardId,
            unlocked_card_number: 2,
        });
        this.interaction?.syncFocusToggleStateFromSpace?.();
    }

    _syncIntroCardPresentation() {
        if (!this.space || this.initialCardOrder.length === 0) return;
        if (!this._isIntroPresentationActive()) return;

        const introCard = this.space.getCard(this.initialCardOrder[0]);

        this.space.removeHighlightCard();

        if (this.space.selectedCard && this.space.selectedCard.id !== introCard.id) {
            this.space.selectedCard.deselect();
            this.space.selectedCard = null;
        }

        if (this.space.focusedCard && this.space.focusedCard.id !== introCard.id) {
            this.space.focusedCard.defocus();
            this.space.focusedCard = null;
        }

        if (introCard.isFaceDown) {
            introCard.faceUp();
        }

        if (!introCard.isFocused) {
            introCard.focus();
        }

        introCard.isHighLifted = false;
        this.space.focusedCard = introCard;
        this.space.highLiftedCard = null;
        this.space.reorderCards(introCard);
        this.space.emitCardStateChanged();
        this.interaction?.syncFocusToggleStateFromSpace?.();
    }

    _syncDeselectedCardZone(previousSelected) {
        if (!previousSelected) return;
        if (this.space.selectedCard?.id === previousSelected.id) return;
        this._syncCardZone(previousSelected);
    }

    _maybeAdvanceFromSelection(card) {
        if (this._getCardNumber(card) === 4) {
            this._advanceLocalState({ unlocked_card_number: 5 });
        }
    }

    _syncCardZone(card) {
        if (!this.state.zone_visible) return;

        const zone = this.space.getZoneForCard(card);
        const zoneId = zone?.id || null;

        if ((this.localCardZones[card.id] || null) === zoneId) return;

        this.localCardZones[card.id] = zoneId;
        this.space.eventBus.pushRemote('cardZoneChanged', {
            id: card.id,
            zone_id: zoneId,
        });
    }

    _applyZoneVisibility() {
        if (!this.space) return;

        if (this.state.zone_visible) {
            if (this.space.allZones.length === 0) {
                this.space.resetZones(OnboardingBehavior.ZONE_LAYOUT);
            }
            this._syncAllZoneMemberships();
        } else if (this.space.allZones.length > 0) {
            this.space.resetZones(null);
        }
    }

    _syncAllZoneMemberships() {
        if (!this.state.zone_visible) return;
        this.space.getAllCards().forEach(card => this._syncCardZone(card));
    }

    _isUnlocked(card) {
        return this._getCardNumber(card) <= this.state.unlocked_card_number;
    }

    _isIntroCard(cardOrId) {
        const id = typeof cardOrId === 'string' ? cardOrId : cardOrId?.id;
        return id === this.initialCardOrder[0];
    }

    _isIntroPresentationActive() {
        return this.state.unlocked_card_number <= 1 || this.state.completed;
    }

    _getCardNumber(card) {
        const id = typeof card === 'string' ? card : card?.id;
        const index = this.initialCardOrder.indexOf(id);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index + 1;
    }

    _advanceLocalState(attrs = {}) {
        const nextState = {
            ...this.state,
            ...attrs,
            unlocked_card_number: Math.max(this.state.unlocked_card_number, attrs.unlocked_card_number || this.state.unlocked_card_number),
            zone_visible: this.state.zone_visible || !!attrs.zone_visible,
        };

        this.applyState(nextState);
    }

    _normalizeState(state = {}) {
        const cardZones = state.card_zones || state.cardZones || {};
        const unlocked = Number(state.unlocked_card_number ?? state.unlockedCardNumber ?? 1);
        return {
            unlocked_card_number: Number.isFinite(unlocked) ? Math.max(1, unlocked) : 1,
            zone_visible: !!(state.zone_visible || state.zoneVisible),
            completed: !!state.completed,
            card_zones: cardZones,
        };
    }
}
